import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Download, Loader2, AlertCircle, Sparkles, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";

const DOWNLOADS_BY_PACKAGE = {
  "picture-day-system": [
    {
      group: "The Guide",
      description: "Read-along guide that walks you through the whole system.",
      files: [
        { label: "Digital (screen-friendly)", file: "/downloads/picture-day-system/guide.pdf" },
        { label: "Print-friendly", file: "/downloads/picture-day-system/guide-print.pdf" },
      ],
    },
    {
      group: "The Templates",
      description: "Fillable + printable pages you'll actually use week to week.",
      files: [
        { label: "Digital (fillable)", file: "/downloads/picture-day-system/templates.pdf" },
        { label: "Print-friendly", file: "/downloads/picture-day-system/templates-print.pdf" },
      ],
    },
    {
      group: "Academic Calendar 2026, 2027",
      description: "The full school-year calendar for reference. Print it, pin it, plan around it.",
      files: [
        { label: "PDF", file: "/downloads/shared/academic-calendar-26-27.pdf" },
      ],
    },
  ],
  "elementary-school-command-center": [
    {
      group: "The Workbook",
      description: "The full workbook, walks you through building your Command Center.",
      files: [
        { label: "Digital (screen-friendly)", file: "/downloads/command-center/workbook.pdf" },
        { label: "Print-friendly", file: "/downloads/command-center/workbook-print.pdf" },
      ],
    },
    {
      group: "The Templates",
      description: "All the plug-and-play templates that go with the workbook.",
      files: [
        { label: "Digital (fillable)", file: "/downloads/command-center/templates.pdf" },
        { label: "Print-friendly", file: "/downloads/command-center/templates-print.pdf" },
      ],
    },
    {
      group: "Academic Calendar 2026, 2027",
      description: "The full school-year calendar for reference. Print it, pin it, plan around it.",
      files: [
        { label: "PDF", file: "/downloads/shared/academic-calendar-26-27.pdf" },
      ],
    },
  ],
  "elementary-school-survival-kit": [
    {
      group: "The Seasonal Guide",
      description: "Follows the actual rhythm of the school year, including the parts that catch parents off guard.",
      files: [
        { label: "Digital (screen-friendly)", file: "/downloads/survival-kit/seasonal-guide.pdf" },
        { label: "Print-friendly", file: "/downloads/survival-kit/seasonal-guide-print.pdf" },
      ],
    },
    {
      group: "The Templates",
      description: "All the plug-and-play templates that go with the seasonal guide.",
      files: [
        { label: "Digital (fillable)", file: "/downloads/survival-kit/templates.pdf" },
        { label: "Print-friendly", file: "/downloads/survival-kit/templates-print.pdf" },
      ],
    },
  ],
};

export default function ThankYouPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const packageHint = params.get("package"); // set by Stripe Payment Link success_url
  const [state, setState] = useState("polling"); // polling | paid | failed | expired | missing
  const [info, setInfo] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("missing");
      return;
    }

    let cancelled = false;
    const MAX = 8;

    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`, {
          params: packageHint ? { package: packageHint } : undefined,
        });
        setInfo(data);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (data.status === "expired") {
          setState("expired");
          return;
        }
        if (attempts.current >= MAX) {
          setState("failed");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        if (attempts.current >= MAX) {
          setState("failed");
          return;
        }
        setTimeout(poll, 2000);
      }
    };
    poll();

    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div data-testid="thank-you-page" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24">
      <Seo title="Thank you" description="Your purchase is confirmed." path="/thank-you" noindex />
      {state === "polling" && (
        <div data-testid="thankyou-polling" className="text-center bg-white rounded-3xl border border-brand-border p-12 shadow-soft">
          <Loader2 className="h-10 w-10 mx-auto animate-spin text-brand-sage" />
          <h1 className="mt-5 font-display text-2xl text-brand-ink">Confirming your payment…</h1>
          <p className="mt-2 text-brand-inkMuted text-sm">This usually takes a few seconds.</p>
        </div>
      )}

      {state === "missing" && (
        <div data-testid="thankyou-missing" className="text-center bg-white rounded-3xl border border-brand-border p-12">
          <AlertCircle className="h-10 w-10 mx-auto text-brand-accent" />
          <h1 className="mt-5 font-display text-2xl text-brand-ink">No checkout session found.</h1>
          <Link to="/products" className="mt-6 inline-flex items-center gap-2 text-brand-sageDark font-medium">
            Back to product <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      )}

      {(state === "failed" || state === "expired") && (
        <div data-testid="thankyou-failed" className="text-center bg-white rounded-3xl border border-brand-border p-12">
          <AlertCircle className="h-10 w-10 mx-auto text-brand-accent" />
          <h1 className="mt-5 font-display text-2xl text-brand-ink">
            {state === "expired" ? "Checkout session expired" : "We couldn't confirm your payment"}
          </h1>
          <p className="mt-3 text-brand-inkMuted">
            If you completed checkout, please check your email, and reach out if anything looks off.
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <Link to="/products" className="inline-flex items-center gap-2 bg-brand-sage text-white hover:bg-brand-sageDark transition-colors px-6 py-3 rounded-full font-medium">
              Try again
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-white border border-brand-border text-brand-ink hover:border-brand-sage transition-colors px-6 py-3 rounded-full font-medium">
              Contact support
            </Link>
          </div>
        </div>
      )}

      {state === "paid" && (
        <div data-testid="thankyou-paid">
          {(() => {
            const downloads = DOWNLOADS_BY_PACKAGE[info?.package_id] || DOWNLOADS_BY_PACKAGE["picture-day-system"];
            return (<>
          <div className="text-center">
            <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-brand-sage text-white">
              <CheckCircle2 className="h-7 w-7" />
            </span>
            <p className="mt-5 text-xs uppercase tracking-[0.22em] font-bold text-brand-sage">
              <Sparkles className="inline h-3.5 w-3.5 mr-1" /> Payment confirmed
            </p>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl font-semibold tracking-tighter text-brand-ink">
              Welcome to the system.
            </h1>
            <p className="mt-4 text-brand-inkSoft max-w-xl mx-auto">
              Thanks for your purchase of <span className="font-medium text-brand-ink">{info?.product_name || "The I Missed Picture Day System"}</span>.
              Your downloads are unlocked below. A receipt and download links are also on their way to your email.
            </p>
          </div>

          <div className="mt-10 bg-white rounded-3xl border border-brand-border p-6 sm:p-8 shadow-soft">
            <h2 className="font-display text-xl font-medium text-brand-ink">Your downloads</h2>
            <p className="mt-1 text-sm text-brand-inkMuted">Both formats are yours, use whichever fits how you work.</p>
            <div className="mt-6 space-y-6">
              {downloads.map((group, gi) => (
                <div
                  key={group.group}
                  data-testid={`thankyou-group-${gi}`}
                  className="rounded-2xl border border-brand-border bg-brand-bg p-5"
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="font-display text-lg font-medium text-brand-ink">{group.group}</h3>
                  </div>
                  <p className="mt-1 text-sm text-brand-inkMuted">{group.description}</p>
                  <ul className="mt-4 divide-y divide-brand-border">
                    {group.files.map((f, fi) => (
                      <li key={f.file} className="py-3 flex items-center justify-between gap-4">
                        <span className="text-brand-ink text-sm sm:text-base">{f.label}</span>
                        <a
                          href={f.file}
                          download
                          data-testid={`thankyou-download-${gi}-${fi}`}
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-sageDark hover:text-brand-ink transition-colors shrink-0"
                        >
                          <Download className="h-4 w-4" /> Download
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8 rounded-2xl bg-brand-bg border border-brand-border p-6 text-center">
            <p className="text-sm text-brand-inkSoft">
              Questions or feedback? <Link to="/contact" className="text-brand-sageDark underline underline-offset-4">Email Lisa directly</Link>.
            </p>
          </div>
            </>);
          })()}
        </div>
      )}
    </div>
  );
}
